import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Хук для проверки прав доступа PBAC на фронтенде
 */
export function usePermissions() {
  const [role, setRole] = useState<string>(() => localStorage.getItem('role') || '');
  const isSuperAdmin = role === 'superadmin';

  const [permissions, setPermissions] = useState<Record<string, string | boolean>>(() => {
    try {
      const raw = localStorage.getItem('permissions');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    const currentRole = localStorage.getItem('role') || '';
    setRole(currentRole);

    if (!currentRole) return;

    // У сотрудника точки role - роль точки, а права - только выданные точкой: роль целиком открыла бы ему все разделы точки.
    // Его права приходят из проверки сессии (тот же расчет, что на сервере)
    const request: Promise<Record<string, string | boolean> | null> = localStorage.getItem('staffLogin')
      ? axios.post('/api/v2/auth/validate-session', { sessionToken: localStorage.getItem('sessionToken') })
        .then(res => (res.data?.valid && res.data.user?.permissions ? res.data.user.permissions : null))
      : axios.get(`/api/v2/roles/${currentRole}`)
        .then(res => (res.data?.success && res.data.role?.permissions ? res.data.role.permissions : null));

    request.then(synced => {
      if (synced) {
        setPermissions(synced);
        localStorage.setItem('permissions', JSON.stringify(synced));
      }
    }).catch(err => {
      console.warn('[usePermissions] Ошибка синхронизации прав роли:', err);
    });
  }, []);

  const hasPerm = useCallback((permCode: string): boolean => {
    // Для прав защиты/ограничений (protect_copy) проверяем реальное значение роли,
    // чтобы супер-админ мог гибко включать/выключать функцию в матрице
    if (permCode.endsWith('.protect_copy')) {
      const val = permissions[permCode];
      return Boolean(val && val !== 'none' && val !== 'false');
    }

    if (isSuperAdmin) return true;
    const val = permissions[permCode];
    return Boolean(val && val !== 'none' && val !== 'false');
  }, [isSuperAdmin, permissions]);

  const getScope = useCallback((permCode: string): string => {
    if (isSuperAdmin && !permCode.endsWith('.protect_copy')) return 'all';
    const val = permissions[permCode];
    if (!val || val === 'none' || val === 'false') return 'none';
    return String(val);
  }, [isSuperAdmin, permissions]);

  return {
    role,
    isSuperAdmin,
    permissions,
    hasPerm,
    getScope
  };
}

export default usePermissions;
